import round from 'lodash/round';
import { Minutes } from '@sdk/types';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Text14 from '@sdk/components/ui/typography/Text14';
import { AutomatedBreakType } from '@sdk/settings/types';
import { AttestationOption } from '@sdk/automatedBreaks/types/Attestation';
import numberOrdinal from '@sdk/utils/numberOrdinal';
import PickerFieldBodyPortal from '@sdk/components/forms/PickerFieldBodyPortal';
import Row from '@sdk/components/ui/spacing/Row';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import TextareaAutoheight from '@sdk/components/forms/TextareaAutoheight';
import { isWaivingAllowed } from '@sdk/automatedBreaks/utils';

import { MissedBreakForAttestation } from '@web/automatedBreaks/breakAttestations/BreakAttestationsModal/breakAttestationsModal.types';

type Props = {
  missedBreak: MissedBreakForAttestation;
  shiftActualDurationInMinutes: Minutes;
  orgSettings: OrgSettings;
  withSeparator: boolean;
  option: AttestationOption;
  text: string;
  skipOrdinals: boolean;
  setOption: (option: AttestationOption) => void;
  setText: (text: string) => void;
  onIsOpenChange: (isOpen: boolean) => void;
};

const AttestationItem = ({
  missedBreak,
  orgSettings,
  withSeparator,
  option,
  setOption,
  text,
  setText,
  onIsOpenChange,
  skipOrdinals,
  shiftActualDurationInMinutes,
}: Props) => {
  const includeWaiveOption = isWaivingAllowed({
    automatedBreakType: missedBreak.type,
    breakNumber: missedBreak.breakNumber,
    currentShiftDurationMinutes: shiftActualDurationInMinutes,
    orgSettings,
  });

  const pickerOptions = [
    {
      value: AttestationOption.taken,
      label: gettext(
        'Yes, I took a%(nth)s %(duration)s-minute %(type)s break before the %(hour)s hour of work ended.',
        {
          nth: skipOrdinals ? '' : ` ${numberOrdinal(missedBreak.breakNumber)}`,
          duration:
            missedBreak.type === AutomatedBreakType.meal
              ? orgSettings.automatedBreaksMealBreakDuration
              : orgSettings.automatedBreaksRestBreakDuration,
          hour: numberOrdinal(
            round(
              (missedBreak.breakNumber *
                (missedBreak.type === AutomatedBreakType.meal
                  ? orgSettings.automatedBreaksMealBreakThreshold
                  : orgSettings.automatedBreaksRestBreakThreshold)) /
                60,
              2
            )
          ),
          type: missedBreak.type,
        }
      ),
    },
    ...(includeWaiveOption
      ? [
          {
            value: AttestationOption.waived,
            label: gettext(
              'I had the opportunity, but I chose not to take a%(nth)s %(type)s break.',
              {
                nth: skipOrdinals ? '' : ` ${numberOrdinal(missedBreak.breakNumber)}`,
                type: missedBreak.type,
              }
            ),
          },
        ]
      : []),
    {
      value: AttestationOption.prevented,
      label: gettext('No, I was prevented from taking a%(nth)s %(type)s break.', {
        nth: skipOrdinals ? '' : ` ${numberOrdinal(missedBreak.breakNumber)}`,
        type: missedBreak.type,
      }),
    },
  ];

  return (
    <Text14 themeColor={ThemeColorType.gray} key={missedBreak.key}>
      {gettext('Did you take your%(nth)s company required %(type)s break?', {
        nth: skipOrdinals ? '' : ` ${numberOrdinal(missedBreak.breakNumber)}`,
        type: missedBreak.type,
      })}
      <Row mTop={10} mBottom={withSeparator || option === AttestationOption.prevented ? 20 : 0}>
        <PickerFieldBodyPortal
          options={pickerOptions}
          value={option}
          onChange={setOption}
          menuWidth={580}
          placeholder={gettext('Select answer')}
          onIsOpenChange={onIsOpenChange}
        />
      </Row>
      {option === AttestationOption.prevented && (
        <Row mBottom={withSeparator ? 20 : 0} mTop={-10}>
          <TextareaAutoheight
            placeholder={gettext('Please specify what happened')}
            value={text}
            onChange={({ target: { value } }): void => {
              setText(value);
            }}
            maxLength={160}
          />
        </Row>
      )}
      {withSeparator && (
        <Row mBottom={20}>
          <HorizontalSeparator />
        </Row>
      )}
    </Text14>
  );
};

export default AttestationItem;
